/* eslint-disable max-len */
/* eslint-disable import/prefer-default-export */

import * as pageActionCreators from "@lululemon/ecom-common/src/store/actions/page-data-actions";
import { trackEvent, EVENT_CATEGORY } from "@lululemon/mwa-analytics";

import fetchPDPResponse, {
  getPdpGraphqlParams,
} from "../graphql/fetchPDPResponse";
import {
  getDataUrl,
  getProductIdFromPath,
  redirectException,
} from "../utils/url";
import * as productActions from "../store/actions/pdp-actions";
import { loadDataRouting } from "../utils/loadDataRouting";
import { pdpLoadDataLogger } from "./loadDataLogger";
import { GQL_PDP_URL_PARAMS_ERROR } from "@lululemon/ecom-common/src/util/graphql-utils";
import { Logger } from "../utils/logger";
import {
  DEFAULT_SORT as REVIEWS_DEFAULT_SORT,
  fetchReviews,
} from "../reviews/index";
import fetchPdpFeaturedCategoryDetails from "../graphql/fetchPdpFeaturedCategoryDetails";
import {
  GQL_RESPONSE_EMPTY,
  RA_PID_LOCALE_REDIRECT,
} from "../constants/loadDataRoutingConstants";

export function handleLegacyInjection(
  legacyPdpData,
  legacyMarkdownConsolidatedPdpData,
  productDetailPageData
) {
  const legacyData = legacyPdpData?.data?.attributes || legacyPdpData || {};
  // CE2-3519: Markdown consolidation epic: Legacy-transformed "markdown
  // consolidated" data needs to be passed along regular data.
  const legacyMarkdownConsolidatedData =
    legacyMarkdownConsolidatedPdpData?.data?.attributes ||
    legacyMarkdownConsolidatedPdpData;
  return {
    ...legacyData,
    productDetailPageData,
    legacyMarkdownConsolidatedData,
  };
}

/**
 * Load data for the container. Server side compatible.
 *
 * @param {string} path - the path to the product being fetched
 * @param {string} queryString - the query string of the api call with `?` prefix
 * @param {object} graphqlClient - the graphql client to be used to make the api call
 * @param {function} dispatch - redux dispatch function to be used to update redux tree after fetch
 * @param {object} options - additional options
 * @param {boolean} options.trackPageLoadAnalytics - boolean to determine whether to fire page load analytics events
 * @param {object} options.req - request object from the server side
 * @param {string} options.nextLocale - Regional Accuracy - locale from next context
 * @param {object} options.ctx - Regional Accuracy - context from next
 * @param {object} options.asPath - Regional Accuracy - path as string
 * @param {boolean} options.shouldFetchPCMData - flag that's passed to fetchPDPResponse() - Footwear
 * @param {object} options.ldFlags - LD flags object
 * @returns {Promise}
 */
export async function loadData(
  path,
  queryString,
  graphqlClient,
  dispatch,
  {
    trackPageLoadAnalytics = true,
    req = undefined,
    shouldFetchPCMData = false,
    nextLocale = "en-us",
    ctx = undefined,
    asPath = "",
    isPreload = false,
    ldFlags = {},
  }
) {
  const productId = getProductIdFromPath(path);

  const seoFeaturedParams = {
    productId,
    locale: nextLocale,
    limit: 7,
  };

  const dataUrl = getDataUrl(path, queryString, nextLocale);

  if (trackPageLoadAnalytics && !isPreload) {
    pageActionCreators.beginPageDataFetch(dataUrl)(dispatch);
  }

  const gqlProductParams = getPdpGraphqlParams(
    path,
    nextLocale,
    queryString,
    req
  );

  try {
    const fetchPDPResponsePromise = fetchPDPResponse(
      gqlProductParams,
      graphqlClient,
      shouldFetchPCMData,
      ldFlags
    );
    const featuredPdpCategoriesPromise = fetchPdpFeaturedCategoryDetails(
      seoFeaturedParams,
      graphqlClient,
      ldFlags?.seoPdpFeaturedCategories
    );

    const [cdpData, featuredCategoryData] = await Promise.all([
      fetchPDPResponsePromise,
      featuredPdpCategoriesPromise,
    ]);
    // TODO 'time' is undefined, it is not part of 'cdpData'.
    const {
      data: legacyPdpData,
      legacyMarkdownConsolidatedData,
      time,
      productDetailPageData,
    } = cdpData;

    const routingException = loadDataRouting({
      gqlProductParams,
      productDetailPageData,
      nextLocale,
      asPath,
    });

    if (routingException && !isPreload) {
      // Initialize the logger
      pdpLoadDataLogger({
        ctx,
        asPath,
        gqlProductParams,
        productDetailPageData,
        code: routingException.code,
        message: routingException.message,
        pidLocale: routingException.pidLocale,
        responseStatusCode: routingException.statusCode,
        responseRedirectUrl: routingException.url,
      });

      // Used to indicate to pdp.js catch block that the error is due to RA
      routingException.loadDataRoutingException = true;
      throw routingException;
    }

    const data = handleLegacyInjection(
      legacyPdpData,
      legacyMarkdownConsolidatedData,
      productDetailPageData
    );

    if (ldFlags?.reviewsAtgSunset && !isPreload) {
      const { reviewsData = {}, bvProdId } = await fetchReviews(
        productDetailPageData?.reviews?.bvId,
        graphqlClient,
        path,
        REVIEWS_DEFAULT_SORT,
        nextLocale,
        "load-data-pdp"
      );

      productDetailPageData.reviews = {
        averageOverallRating:
          reviewsData?.reviewProductStatistics?.averageOverallRating || 0,
        bvId: bvProdId,
        count: reviewsData?.reviewProductStatistics?.totalReviewCount || 0,
        reviewsData,
      };
    }

    data.isOnlineInventoryCached = false;
    data.isPriceConsolidatedOnlineInventoryCached = false; // CE2-3519 ABT
    data.featuredCategories = featuredCategoryData;

    // If the code reaches here, page will be rendered, congratulations!
    // SAVE CURRENT PRODUCT TO REDUX
    productActions.setProduct(
      { data, loaded: true, timestamp: Date.now() },
      queryString,
      dataUrl
    )(dispatch);
    // Removed as setProduct already adds its data to the pages redux store.
    // pageActionCreators.addPageData(dataUrl, { data, loaded: true })(dispatch);

    if (trackPageLoadAnalytics) {
      pageActionCreators.completePageDataFetch(dataUrl)(dispatch);
    }

    return { data, time };
  } catch (error) {
    if (
      nextLocale === "en-us" &&
      error?.cause?.message === GQL_RESPONSE_EMPTY &&
      error?.cause?.code === 404
    ) {
      // check if Canada PCM has anything for this
      const gqlProductParamsCA = { ...gqlProductParams, locale: "en-ca" };

      let pdpResponseCA;
      try {
        pdpResponseCA = await fetchPDPResponse(
          gqlProductParamsCA,
          graphqlClient,
          shouldFetchPCMData,
          ldFlags
        );
      } catch (error) {
        // do nothing, let pdpResponseCA be undefined
      }

      if (pdpResponseCA) {
        // we got good data from PCM on Canada, redirect
        throw redirectException({
          message: RA_PID_LOCALE_REDIRECT,
          url: `/en-ca${asPath}`,
        });
      }
    }
    if (error?.isRedirect && !isPreload) {
      if (error.url?.includes("outofstock") && typeof window !== "undefined") {
        trackEvent(EVENT_CATEGORY.ERROR, {
          error: {
            errorType: "oos-302",
            errorDetails: "Don’t sweat it. We have more gear that you’ll love.",
            errorGuestFacing: true,
            errorMessage: "Oh snap! Looks like we’re all out.",
          },
        });
      }
      throw error;
    }
    if (error?.statusCode === 404 && typeof window !== "undefined") {
      trackEvent(EVENT_CATEGORY.ERROR, {
        error: {
          errorType: "oos-404",
          errorDetails: "We can't find the page you're looking for.",
          errorGuestFacing: true,
          errorMessage: "We're sorry.",
        },
      });
    }
    if (trackPageLoadAnalytics && !isPreload) {
      pageActionCreators.completePageDataFetch(dataUrl)(dispatch);
    }
    if (error?.loadDataRoutingException && !error?.isFatal && !isPreload) {
      throw error;
    } else {
      // Although the event is fatal, we don't need to send to Sentry as this evaluates SSR only.
      // It can be monitored and alerted in AWS or Splunk.
      if (isPreload && error?.message !== GQL_PDP_URL_PARAMS_ERROR) {
        Logger.error(
          new Error(
            `Error preloading product data: failed network call ${
              error?.message || error
            }`
          ),
          {
            cause: error,
            isPreload: true,
            isRedirect: error?.isRedirect,
            req_url: dataUrl,
            tags: { functionName: "logic.loadData" },
          }
        );
      } else {
        Logger.error(
          new Error(
            `PDP API Error - failed network call ${error?.message || error}`
          ),
          {
            req_url: dataUrl,
            isMainPurchaseFlow: true,
            isRedirect: error.isRedirect,
            cause: error,
            tags: { functionName: "logic.loadData" },
            req,
          }
        );
      }

      if (error?.isRedirect && !isPreload) {
        throw redirectException();
      }

      if (error?.raw?.status >= 400 && error?.raw?.status !== 404) {
        error.message = `Could not load PDP data. Received ${error.raw.status} status code on API response.`;
      }

      if (!isPreload) {
        throw error;
      }
    }
  }
}
